import React from 'react';
import { Label } from '../../ui/Label/Label';
import { Input } from '../../ui/Input/Input';
import { Text } from '../../ui/Text/Text';
import { cn } from '../../../lib/cn';

/**
 * FormField component combining Label + Input + Error/Help text
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} props.label - Field label
 * @param {string} [props.name] - Input name
 * @param {string} [props.type='Text'] - Input type (Text, Email, Password, Number, Tel, URL, etc.)
 * @param {string} [props.placeholder] - Input placeholder
 * @param {string} [props.value] - Input value
 * @param {Function} [props.onChange] - Change handler (e) => void
 * @param {Function} [props.onInput] - Input handler (e) => void
 * @param {Function} [props.onBlur] - Blur handler (e) => void
 * @param {boolean} [props.required=false] - Required field
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {boolean} [props.readonly=false] - Readonly state
 * @param {string} [props.error] - Error message
 * @param {string} [props.helpText] - Help text (shown when no error)
 * @param {string} [props.valueState] - Value state (None, Success, Warning, Error, Information)
 * @param {React.ReactNode} [props.children] - Custom input component (replaces default Input)
 * @param {string} [props.icon] - Icon name
 * @param {number} [props.maxlength] - Max length
 * @returns {JSX.Element}
 *
 * @example
 * // Basic usage
 * <FormField
 *   label="Email"
 *   type="Email"
 *   name="email"
 *   placeholder="Enter your email"
 *   value={email}
 *   onInput={(e) => setEmail(e.target.value)}
 *   required
 *   error={emailError}
 *   helpText="We'll never share your email"
 * />
 *
 * @example
 * // With custom input
 * <FormField label="Country" required error={countryError}>
 *   <Select
 *     value={country}
 *     onChange={(e) => setCountry(e.detail.selectedOption.value)}
 *     options={countries}
 *   />
 * </FormField>
 */
export function FormField({
  className,
  label,
  name,
  type = 'Text',
  placeholder,
  value,
  onChange,
  onInput,
  onBlur,
  required = false,
  disabled = false,
  readonly = false,
  error,
  helpText,
  valueState,
  children,
  icon,
  maxlength,
  ...props
}) {
  // Determine value state
  const computedValueState = error ? 'Error' : valueState || 'None';

  // Generate unique ID for label-input association
  const fieldId = React.useId();

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Label */}
      <Label required={required} htmlFor={fieldId}>
        {label}
      </Label>

      {/* Input or custom children */}
      {children ? (
        <div className="w-full">{children}</div>
      ) : (
        <Input
          id={fieldId}
          name={name || label.toLowerCase().replace(/\s+/g, '-')}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onInput={onInput}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          readonly={readonly}
          valueState={computedValueState}
          icon={icon}
          maxlength={maxlength}
          className="w-full"
          {...props}
        />
      )}

      {/* Error or Help Text */}
      {error ? (
        <Text className="text-sm text-red-600">{error}</Text>
      ) : helpText ? (
        <Text className="text-sm text-gray-500">{helpText}</Text>
      ) : null}
    </div>
  );
}
